/**
 * PM Notifications Component
 * Shows notifications for PM and Owner
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Chip,
    IconButton,
    Typography,
    Button,
    Badge,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Notifications,
    CheckCircle,
    Payment,
    LocationOn,
    PersonAdd,
    Settings,
    MarkEmailRead,
    Delete
} from '@mui/icons-material';
import projectManagerApi from '../../services/projectManagerApi';

const PMNotifications = ({ onNotificationClick }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        loadNotifications();
        // Refresh every 30 seconds
        const interval = setInterval(loadNotifications, 30000);
        return () => clearInterval(interval);
    }, []);

    const loadNotifications = async () => {
        try {
            setLoading(true);
            const response = await projectManagerApi.getNotifications(false);
            if (response.success) {
                setNotifications(response.data.notifications || []);
                setUnreadCount(response.data.unreadCount || 0);
            }
        } catch (err) {
            console.error('Error loading notifications:', err);
            setError('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const handleMarkRead = async (notificationId) => {
        try {
            await projectManagerApi.markNotificationRead(notificationId);
            loadNotifications();
        } catch (err) {
            console.error('Error marking notification read:', err);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await projectManagerApi.markAllNotificationsRead();
            loadNotifications();
        } catch (err) {
            console.error('Error marking all notifications read:', err);
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'payment_request':
            case 'payment_approved':
            case 'payment_rejected':
            case 'payment_auto_approved':
                return <Payment />;
            case 'site_visit':
                return <LocationOn />;
            case 'pm_application':
            case 'pm_approved':
            case 'pm_rejected':
                return <PersonAdd />;
            case 'authority_change':
                return <Settings />;
            default:
                return <Notifications />;
        }
    };

    const getNotificationColor = (type) => {
        switch (type) {
            case 'payment_approved':
            case 'pm_approved':
                return 'success';
            case 'payment_rejected':
            case 'pm_rejected':
                return 'error';
            case 'payment_request':
                return 'warning';
            default:
                return 'primary';
        }
    };

    if (loading && notifications.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    Notifications
                    {unreadCount > 0 && (
                        <Badge badgeContent={unreadCount} color="error" sx={{ ml: 2 }}>
                            <Notifications />
                        </Badge>
                    )}
                </Typography>
                {unreadCount > 0 && (
                    <Button size="small" onClick={handleMarkAllRead}>
                        Mark All Read
                    </Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {notifications.length === 0 ? (
                <Alert severity="info">No notifications</Alert>
            ) : (
                <List>
                    {notifications.map((notification) => (
                        <ListItem
                            key={notification.id}
                            sx={{
                                bgcolor: notification.read ? 'background.paper' : 'action.hover',
                                mb: 1,
                                borderRadius: 1,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'action.selected' }
                            }}
                            onClick={() => {
                                if (!notification.read) {
                                    handleMarkRead(notification.id);
                                }
                                if (onNotificationClick && notification.action_url) {
                                    onNotificationClick(notification.action_url);
                                }
                            }}
                        >
                            <ListItemAvatar>
                                <Avatar sx={{ bgcolor: `${getNotificationColor(notification.type)}.main` }}>
                                    {getNotificationIcon(notification.type)}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="subtitle2">
                                            {notification.title}
                                        </Typography>
                                        {!notification.read && (
                                            <Chip label="New" size="small" color="error" />
                                        )}
                                    </Box>
                                }
                                secondary={notification.message}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
                                {new Date(notification.created_at).toLocaleString()}
                            </Typography>
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
};

export default PMNotifications;



